import { useEffect, useState, useCallback } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  Grid,
  HStack,
  Input,
  Textarea,
  VStack,
} from '@chakra-ui/react'
import { useToastNotification } from '@/lib/utils/useToastNotification'
import { apiClient } from '@/lib/api/client'

export type CategoryFormProps = {
  categoryId?: number
  onSuccess?: () => void
  onCancel?: () => void
}

const initialValues = {
  name: '',
  slug: '',
  descriptions: '',
  active: true,
}

function slugify(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
}

/**
 * Machine Type / asset Category form. Maps to the "Category" dropdown in
 * StoreAssetForm (the asset's machine type, e.g.
 * "POS Terminal", "Printer"). Backend forces parent_id to the fixed
 * "machine type" anchor category — the form never sends parent_id.
 */
export function CategoryForm({ categoryId, onSuccess, onCancel }: CategoryFormProps) {
  const { showError, showSuccess } = useToastNotification()
  const [loading, setLoading] = useState(!!categoryId)
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<Record<string, string[]>>({})
  const [form, setForm] = useState(initialValues)
  const [slugTouched, setSlugTouched] = useState(false)

  const loadOne = useCallback(async () => {
    if (!categoryId) return
    setLoading(true)
    setErrors({})
    try {
      const { data } = await apiClient.get(`/master-data/categories/${categoryId}`)
      const r = data?.data ?? data
      setForm({
        name: r.name ?? '',
        slug: r.slug ?? '',
        descriptions: r.descriptions ?? '',
        active: r.active !== false,
      })
      setSlugTouched(true)
    } catch (e: unknown) {
      showError(e as Error)
    } finally {
      setLoading(false)
    }
  }, [categoryId, showError])

  useEffect(() => {
    if (categoryId) {
      loadOne()
    } else {
      setForm(initialValues)
      setSlugTouched(false)
    }
  }, [categoryId, loadOne])

  const updateField = (field: keyof typeof form, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }))
    if (errors[field]) {
      setErrors((prev) => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }

  const handleNameChange = (value: string) => {
    setForm((prev) => ({
      ...prev,
      name: value,
      slug: slugTouched ? prev.slug : slugify(value),
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrors({})
    setSaving(true)
    try {
      const payload = {
        name: form.name.trim(),
        slug: (form.slug.trim() || slugify(form.name)).trim(),
        descriptions: form.descriptions.trim() || null,
        active: form.active,
      }
      if (categoryId) {
        await apiClient.put(`/master-data/categories/${categoryId}`, payload)
        showSuccess('Success', 'Category updated successfully')
      } else {
        await apiClient.post('/master-data/categories', payload)
        showSuccess('Success', 'Category created successfully')
      }
      onSuccess?.()
    } catch (err: unknown) {
      const res = (err as { response?: { data?: { errors?: Record<string, string[]> } } })?.response?.data
      if (res?.errors && typeof res.errors === 'object') {
        setErrors(res.errors)
      } else {
        showError(err as Error)
      }
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <Box py={8}>
        <Box as="span">Loading...</Box>
      </Box>
    )
  }

  return (
    <Box as="form" onSubmit={handleSubmit}>
      <VStack spacing={4} align="stretch">
        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Name <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.name?.length} isRequired>
            <Input
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="e.g. POS Terminal, Printer, Laptop"
            />
            <FormErrorMessage>{errors.name?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">
            Slug <Box as="span" color="red.500">*</Box>
          </FormLabel>
          <FormControl isInvalid={!!errors.slug?.length} isRequired>
            <Input
              value={form.slug}
              onChange={(e) => {
                setSlugTouched(true)
                updateField('slug', slugify(e.target.value))
              }}
              placeholder="auto-generated from name"
            />
            <FormHelperText>URL-friendly identifier. Auto-filled from Name, but editable.</FormHelperText>
            <FormErrorMessage>{errors.slug?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="start">
          <FormLabel pt={2} fontWeight="medium">Description</FormLabel>
          <FormControl isInvalid={!!errors.descriptions?.length}>
            <Textarea
              value={form.descriptions}
              onChange={(e) => updateField('descriptions', e.target.value)}
              placeholder="Optional description"
              rows={3}
            />
            <FormErrorMessage>{errors.descriptions?.[0]}</FormErrorMessage>
          </FormControl>
        </Grid>

        <Grid templateColumns={{ base: '1fr', md: '3fr 9fr' }} gap={4} alignItems="center">
          <FormLabel pt={2} fontWeight="medium" mb={0}>Active</FormLabel>
          <FormControl>
            <Checkbox
              isChecked={form.active}
              onChange={(e) => updateField('active', e.target.checked)}
              colorScheme="blue"
            >
              <Box as="span">Available for selection on store assets and brand mapping.</Box>
            </Checkbox>
          </FormControl>
        </Grid>

        <HStack spacing={4} justify="flex-end" pt={4}>
          {onCancel && (
            <Button variant="outline" onClick={onCancel} isDisabled={saving}>
              Cancel
            </Button>
          )}
          <Button type="submit" colorScheme="blue" isLoading={saving} loadingText={categoryId ? 'Saving...' : 'Creating...'}>
            {categoryId ? 'Save' : 'Create Category'}
          </Button>
        </HStack>
      </VStack>
    </Box>
  )
}
